/* eslint-disable react/no-unescaped-entities */
/**
 * FreedrIQIntroModal — first-visit gate on the actual `/freedriq` assessment
 * page. Explains what FreedrIQ measures, why the pre-learning attempt is
 * critical for the growth report, and only THEN lets the user start.
 *
 * Skippable — dismissal is remembered per-user so returning learners don't
 * get nagged. Distinct from the /learning FreedrIQSplash which invites new
 * users onto FreedrIQ; this one gates the assessment itself.
 */
import React, { useEffect, useState } from 'react';
import { Sparkles, Timer, TrendingUp, ShieldCheck, GraduationCap } from 'lucide-react';
import { Button } from './ui/button';
import { Dialog, DialogContent, DialogTitle, DialogDescription } from './ui/dialog';

const LS_KEY = 'freedriq:intro_seen';

const FreedrIQIntroModal = ({ onSkip }) => {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    try {
      if (localStorage.getItem(LS_KEY)) return;
      setOpen(true);
    } catch { setOpen(true); }
  }, []);

  const remember = () => {
    try { localStorage.setItem(LS_KEY, '1'); } catch { /* ignore */ }
  };

  const startNow = () => { remember(); setOpen(false); };
  const skipForNow = () => { remember(); setOpen(false); onSkip?.(); };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className="max-w-lg" data-testid="freedriq-intro-modal">
        <div className="eyebrow text-primary inline-flex items-center gap-1.5">
          <Sparkles className="w-3 h-3" /> Before you start
        </div>
        <DialogTitle className="font-heading text-2xl font-extrabold mt-1">
          What FreedrIQ is — and why to take it now
        </DialogTitle>
        <DialogDescription className="text-sm text-muted-foreground">
          It's a two-part diagnostic, not a certification. It exists so the platform can
          personalise your learning path — and it only works if you take it before you
          start the courses.
        </DialogDescription>

        <ul className="mt-3 space-y-2.5 text-sm text-foreground">
          <li className="flex items-start gap-2">
            <TrendingUp className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span><strong>Growth report requires this "pre" attempt.</strong> After
              you finish the courses you retake FreedrIQ; the two scores side-by-side
              become your <em>growth report</em>. Skip pre → no growth report ever.</span>
          </li>
          <li className="flex items-start gap-2">
            <GraduationCap className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span><strong>Personalised study plan.</strong> Prof. FreedrIQ uses your
              answers to rank the lessons you should tackle first. Skip → generic plan.</span>
          </li>
          <li className="flex items-start gap-2">
            <Timer className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span><strong>~7 minutes.</strong> Two short sections — a self-assessment
              (7 questions) and a knowledge test (25 questions). No time pressure, no
              wrong-answer penalties.</span>
          </li>
          <li className="flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
            <span><strong>Private.</strong> Your score is never displayed on your
              public community profile. Only you (and the platform's recommendation
              engine) see it.</span>
          </li>
        </ul>

        <div className="mt-5 flex items-center justify-between gap-2 flex-wrap">
          <button
            type="button"
            onClick={skipForNow}
            className="text-xs font-semibold underline text-muted-foreground hover:text-foreground"
            data-testid="freedriq-intro-skip"
          >
            Skip for now — I'll come back
          </button>
          <Button
            onClick={startNow}
            className="rounded-full font-bold px-5"
            data-testid="freedriq-intro-start"
          >
            Got it — start the diagnostic
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default FreedrIQIntroModal;
